<template>
  <div class="w-mobile-days" aria-label="Дни недели">
    <button
      v-for="day in store.weekDays"
      :key="day"
      type="button"
      :aria-pressed="String(day === store.date)"
      :aria-label="formatDayTitle(day)"
      @click="store.pickDay(day)"
    >
      <span>{{ formatDate(day, { weekday: 'short' }) }}</span>
      <strong>{{ formatDate(day, { day: 'numeric' }) }}</strong>
    </button>
  </div>

  <div
    ref="tableWrap"
    class="w-table-wrap"
    @scroll="store.closeRoomMenu()"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
  >
    <table
      class="w-table"
      aria-label="Недельный календарь приёма: время по строкам, дни по столбцам"
    >
      <thead>
        <tr>
          <th scope="col" class="w-time-head">Время</th>
          <th
            v-for="{ day, hours } in dayHeads"
            :key="day"
            scope="col"
            class="w-day-head"
            :class="{ 'is-current': day === store.date }"
          >
            <button type="button" :aria-label="formatDayTitle(day)" @click="store.pickDay(day)">
              <span>{{ formatDate(day, { weekday: 'short' }) }}</span>
              <strong>{{ formatDate(day, { day: 'numeric' }) }}</strong>
            </button>
            <span class="w-day-hours">
              <template v-if="hours.open.length">
                <span class="w-hours-label">Работа </span>
                <span class="w-hours-value">{{ hoursText(hours.open) }}</span>
                <br />
                <span class="w-hours-label">Приём </span>
                <span class="w-hours-value">{{ hoursText(hours.intake) }}</span>
              </template>
              <template v-else>Закрыто</template>
              <template v-if="hours.isException">
                <br />
                {{ hours.label || 'Особый режим' }}
              </template>
            </span>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-if="!rows.length">
          <td :colspan="store.displayDays.length + 1" class="w-week-closed">
            {{ store.displayDays.length === 1 ? 'В этот день' : 'В эти дни' }}
            филиал закрыт. Выберите другую дату.
          </td>
        </tr>
        <template v-else>
          <tr v-for="row in rows" :key="row.start" :style="{ height: `${row.height}px` }">
            <th scope="row" class="w-time">{{ row.start }}</th>
            <td
              v-for="cell in row.cells"
              :key="cell.day"
              class="w-day-cell"
              :class="{ 'w-range-selected': isInRange(cell), 'w-weekend': isWeekend(cell.day) }"
              :rowspan="cell.rowspan"
              :data-range-date="cell.day"
              :data-range-start="cell.start"
              :data-range-end="cell.end"
            >
              <div
                v-if="cell.kind === 'slot'"
                class="w-slot"
                :class="{ 'is-selected': isSelected('slot', cell.slot.id) }"
                :data-color="cell.status.color"
                :style="{ minHeight: `${cell.height}px` }"
              >
                <button
                  type="button"
                  class="w-room-button"
                  :data-room-slot="cell.slot.id"
                  :aria-label="`Кабинет ${store.roomNumber(cell.slot.roomId)}, `
                    + `${cell.slot.date} ${cell.slot.start}–${cell.slot.end}. Сменить кабинет`"
                  :aria-expanded="String(store.roomMenuSlotId === cell.slot.id)"
                  @click="store.toggleRoomMenu(cell.slot.id)"
                >
                  <span class="w-room-num">{{ store.roomNumber(cell.slot.roomId) }}</span>
                  <q-icon name="expand_more" />
                </button>
                <button
                  type="button"
                  class="w-slot-info"
                  :aria-label="`${cell.slot.start}–${cell.slot.end}. `
                    + `${cell.status.label}. Открыть приём`"
                  @click="onCellClick(() => store.selectSlot(cell.slot.id))"
                >
                  <span class="w-status-full">{{ cell.status.full }}</span>
                  <span class="w-status-short" aria-hidden="true">{{ cell.status.short }}</span>
                  <span class="w-slot-time">{{ cell.slot.start }}–{{ cell.slot.end }}</span>
                </button>
              </div>

              <button
                v-else-if="cell.kind === 'break'"
                type="button"
                class="w-break"
                :style="{ minHeight: `${cell.height}px` }"
                :aria-label="`${cell.rest.label} ${cell.rest.start}–${cell.rest.end}`"
                @click="onCellClick(() => store.selectBreak(cell.rest.id))"
              >
                <q-icon :name="breakIcon(cell.rest.label)" />
                <span>{{ cell.rest.label }}</span>
              </button>

              <button
                v-else-if="cell.kind === 'empty'"
                type="button"
                class="w-empty"
                :style="{ minHeight: `${cell.height}px` }"
                :disabled="!store.canEdit"
                :aria-label="`Добавить приём ${cell.day} ${cell.start}–${cell.end}`"
                @click="onCellClick(() => store.selectRange(cell.day, cell.start, cell.end))"
              >
                <span aria-hidden="true">+</span>
              </button>

              <div
                v-else
                class="w-closed-cell"
                :style="{ minHeight: `${cell.height}px` }"
                :aria-label="cell.reason"
              >
                {{ cell.text }}
              </div>
            </td>
          </tr>
          <tr class="w-end-row">
            <th scope="row" class="w-time">{{ store.viewHours.to }}</th>
            <td v-for="day in store.displayDays" :key="day" />
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import {
  computed, onBeforeUnmount, onMounted, ref,
} from 'vue';
import { useScheduleStore } from '@/store/calendar/scheduleModule';
import {
  checkBranchAccess,
  formatDate,
  formatDayTitle,
  hoursText,
  isWeekend,
  toMinutes,
  toTime,
} from '~utils/calendarModel';
import type {
  CalendarBreak, CellColor, Slot, TimeInterval,
} from '@/store/calendar/types';

type SlotStatus = {
  color: CellColor;
  label: string;
  full: string;
  short: string;
};

type CellBase = {
  day: string;
  start: string;
  end: string;
  rowspan: number;
  height: number;
};

type GridCell = CellBase & (
  | { kind: 'slot'; slot: Slot; status: SlotStatus }
  | { kind: 'break'; rest: CalendarBreak }
  | { kind: 'empty' }
  | { kind: 'closed'; reason: string; text: string }
);

type GridRow = {
  start: string;
  height: number;
  cells: GridCell[];
};

type Range = TimeInterval & { date: string };

type DragState = Range & {
  x: number;
  y: number;
  moved: boolean;
};

// Высота получасового интервала, px; короче строка не бывает
const HALF_HOUR_HEIGHT = 26;
const GRID_STEP = 30;
const DRAG_THRESHOLD = 6;
const CLICK_SUPPRESS_MS = 200;

const store = useScheduleStore();
const tableWrap = ref<HTMLElement | null>(null);

const dayHeads = computed(() => store.displayDays.map((day) => ({
  day,
  hours: store.branchDay(day),
})));

const ownSlots = computed(() => store.schedule.slots.filter(
  (slot) => slot.staffId === store.staffId,
));

const ownBreaks = computed(() => store.schedule.breaks.filter(
  (rest) => rest.staffId === store.staffId,
));

// Границы строк: сетка по 30 минут плюс все начала/концы приёмов, перерывов
// и интервалов режима филиала — чтобы каждый элемент вставал ровно в свои строки
const timeMarks = computed<number[]>(() => {
  const { from, to } = store.viewHours;
  if (!from || !to) return [];
  const low = toMinutes(from);
  const high = toMinutes(to);
  const marks = new Set([low, high]);
  const addMark = (value: string) => {
    const minute = toMinutes(value);
    if (minute > low && minute < high) marks.add(minute);
  };

  for (let minute = Math.ceil(low / GRID_STEP) * GRID_STEP; minute < high; minute += GRID_STEP) {
    marks.add(minute);
  }
  [...ownSlots.value, ...ownBreaks.value]
    .filter((item) => store.displayDays.includes(item.date))
    .forEach((item) => {
      addMark(item.start);
      addMark(item.end);
    });
  store.displayDays.forEach((day) => {
    const { open, intake } = store.branchDay(day);
    [...open, ...intake].forEach((item) => {
      addMark(item.start);
      addMark(item.end);
    });
  });

  return [...marks].sort((a, b) => a - b);
});

const rowHeights = computed(() => timeMarks.value.slice(0, -1).map((minute, index) => (
  Math.max(HALF_HOUR_HEIGHT, ((timeMarks.value[index + 1] - minute) * HALF_HOUR_HEIGHT) / GRID_STEP)
)));

const getSlotStatus = (slot: Slot): SlotStatus => {
  const availability = store.slotAvailability(slot);
  const { color, branchBlocked } = availability;
  const ownPatient = store.schedule.appointments.some((item) => item.slotId === slot.id);
  const closedText = branchBlocked ? 'Вне приёма' : 'Закрыт';
  const statuses: Record<CellColor, Omit<SlotStatus, 'color'>> = {
    red: { label: ownPatient ? 'Пациент' : 'Пациент коллеги', full: 'Пациент', short: 'Запись' },
    gray: {
      label: branchBlocked ? 'Вне приёма' : 'Кабинет закрыт',
      full: closedText,
      short: 'Закрыт',
    },
    yellow: { label: 'Коллеги', full: 'Коллеги', short: 'Колл.' },
    green: { label: 'Свободно', full: 'Свободно', short: 'Своб.' },
  };
  return { color, ...statuses[color] };
};

const covers = (item: TimeInterval & { date: string }, day: string, minute: number) => (
  item.date === day && toMinutes(item.start) <= minute && toMinutes(item.end) > minute
);

const buildCell = (day: string, index: number): GridCell | null => {
  const marks = timeMarks.value;
  const minute = marks[index];
  const low = marks[0];
  const high = marks[marks.length - 1];
  const slot = ownSlots.value.find((item) => covers(item, day, minute));
  const rest = ownBreaks.value.find((item) => covers(item, day, minute));
  const item = slot || rest;

  // Ячейку уже заняла предыдущая строка через rowspan
  if (item && Math.max(toMinutes(item.start), low) !== minute) return null;

  const finish = item ? Math.min(toMinutes(item.end), high) : marks[index + 1];
  const lastIndex = marks.indexOf(finish);
  const base: CellBase = {
    day,
    start: toTime(minute),
    end: toTime(finish),
    rowspan: lastIndex - index,
    height: rowHeights.value.slice(index, lastIndex).reduce((sum, value) => sum + value, 0) - 3,
  };

  if (slot) {
    return {
      ...base, kind: 'slot', slot, status: getSlotStatus(slot),
    };
  }
  if (rest) return { ...base, kind: 'break', rest };

  const access = checkBranchAccess(store.schedule, store.rooms, {
    date: day,
    start: base.start,
    end: base.end,
    roomId: store.defaultRoomId,
  });
  if (access.ok) return { ...base, kind: 'empty' };

  const duringOpen = store.branchDay(day).open.some(
    (interval) => toMinutes(interval.start) <= minute && toMinutes(interval.end) >= finish,
  );
  return {
    ...base,
    kind: 'closed',
    reason: access.message,
    text: duringOpen ? 'Нет приёма' : 'Закрыто',
  };
};

const rows = computed<GridRow[]>(() => timeMarks.value.slice(0, -1).map((minute, index) => ({
  start: toTime(minute),
  height: rowHeights.value[index],
  cells: store.displayDays
    .map((day) => buildCell(day, index))
    .filter((cell): cell is GridCell => cell !== null),
})));

const breakIcon = (label: string) => (label.toLowerCase().includes('проветр') ? 'air' : 'coffee');

const isSelected = (kind: 'slot' | 'break', id: number) => (
  store.selection?.kind === kind && store.selection.id === id
);

// --- Выделение интервала протягиванием мыши ---

let drag: DragState | null = null;
let suppressClickUntil = 0;
const dragRange = ref<Range | null>(null);

const isInRange = (cell: CellBase) => {
  const range = dragRange.value || store.selection;
  return !!range
    && cell.day === range.date
    && toMinutes(cell.start) < toMinutes(range.end)
    && toMinutes(cell.end) > toMinutes(range.start);
};

// Клик, которым закончилось протягивание, не должен открывать ячейку
const onCellClick = (action: () => void) => {
  if (performance.now() < suppressClickUntil) return;
  action();
};

const findCell = (target: EventTarget | null) => (
  (target as HTMLElement | null)?.closest<HTMLElement>('[data-range-date]') || null
);

const onPointerDown = (event: PointerEvent) => {
  if (store.busy || !store.canEdit || event.pointerType !== 'mouse' || event.button !== 0) return;
  const target = event.target as HTMLElement;
  if (!target.closest('.w-empty, .w-slot-info, .w-break')) return;
  const cell = findCell(target);
  if (!cell) return;
  const { rangeDate = '', rangeStart = '', rangeEnd = '' } = cell.dataset;
  drag = {
    date: rangeDate,
    start: rangeStart,
    end: rangeEnd,
    x: event.clientX,
    y: event.clientY,
    moved: false,
  };
};

const onPointerMove = (event: PointerEvent) => {
  if (!drag) return;
  // Левая кнопка отпущена за пределами таблицы
  if (event.buttons % 2 === 0) {
    drag = null;
    return;
  }
  const distance = Math.abs(event.clientY - drag.y) + Math.abs(event.clientX - drag.x);
  if (distance < DRAG_THRESHOLD) return;
  const cell = findCell(document.elementFromPoint(event.clientX, event.clientY));
  if (!cell || !tableWrap.value?.contains(cell) || cell.dataset.rangeDate !== drag.date) return;
  const { rangeStart = '', rangeEnd = '' } = cell.dataset;
  drag.moved = true;
  dragRange.value = {
    date: drag.date,
    start: toTime(Math.min(toMinutes(drag.start), toMinutes(rangeStart))),
    end: toTime(Math.max(toMinutes(drag.end), toMinutes(rangeEnd))),
  };
  event.preventDefault();
};

const onPointerUp = () => {
  const range = dragRange.value;
  if (drag?.moved && range) {
    suppressClickUntil = performance.now() + CLICK_SUPPRESS_MS;
    store.selectRange(range.date, range.start, range.end);
  }
  drag = null;
  dragRange.value = null;
};

onMounted(() => window.addEventListener('pointerup', onPointerUp));
onBeforeUnmount(() => window.removeEventListener('pointerup', onPointerUp));
</script>
